import {
  Box,
  Button,
  Flex,
  IconButton,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

interface SensorAlertTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Name of the first selected sensor shown as example in the reading line */
  sensorName?: string;
  /** Formatted condition strings (e.g. "Chuva acumulada (24h) > 20 mm") */
  conditions?: string[];
  /** Total number of selected sensors */
  totalSensors?: number;
  /** Name of the event being configured */
  eventName?: string;
  /** Location area name of the event */
  eventLocationName?: string;
}

export const SensorAlertTemplateModal: React.FC<SensorAlertTemplateModalProps> = ({
  isOpen,
  onClose,
  sensorName,
  conditions,
  totalSensors = 1,
  eventName,
  eventLocationName,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.sensorAlert",
  });

  const displaySensorName = sensorName ?? t("templateExampleSensorName");
  const displayEventName = eventName ?? t("templateExampleEventName");
  const displayLocation = eventLocationName ?? t("templateExampleLocation");

  return (
    <Modal
      closeOnOverlayClick={false}
      size="lg"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent borderRadius="8px" border="1px solid #E4E4E7" position="relative">
        {/* Custom close button */}
        <IconButton
          aria-label={t("closeAriaLabel")}
          icon={<X size={16} />}
          variant="ghost"
          size="sm"
          position="absolute"
          top="7px"
          right="16px"
          minW="29px"
          h="29px"
          borderRadius="30px"
          bg="rgba(0, 0, 0, 0.5)"
          color="white"
          _hover={{ bg: "rgba(0, 0, 0, 0.7)" }}
          onClick={onClose}
        />

        <ModalBody p="24px" display="flex" flexDir="column" gap="20px">
          {/* Header */}
          <Text fontSize="18px" fontWeight="600" color="#484848" pr="40px">
            {t("templateModalTitle")}
          </Text>

          {/* Message preview box */}
          <Box bg="#F6F6F6" p="16px" borderRadius="8px" display="flex" flexDir="column" gap="12px">
            <Text fontSize="12px" color="#086AA9" fontWeight="500">
              {t("templateExampleLabel")}
            </Text>

            {/* Alert title line */}
            <Text fontWeight="700" fontSize="15px" color="#1A1A1A">
              {t("templateAlertTitle")}
            </Text>

            {/* Main reading example */}
            <Text fontSize="14px" color="#484848">
              {t("templateMainReading", { sensorName: displaySensorName })}
            </Text>

            {/* Sensor readings section */}
            <Box>
              <Text fontWeight="600" fontSize="14px" color="#1A1A1A" mb="6px">
                {t("templateSensorReadingsTitle")}
              </Text>
              <List spacing={2}>
                {conditions && conditions.length > 0 ? (
                  conditions.map((condition, index) => (
                    <ListItem
                      key={index}
                      fontSize="14px"
                      color="#484848"
                      display="flex"
                      alignItems="flex-start"
                      gap="6px"
                    >
                      <Text as="span" flexShrink={0}>•</Text>
                      <Text as="span">
                        {t("templateSensorReadingsBullet", {
                          activeSensors: Math.ceil(totalSensors / 2),
                          totalSensors,
                          condition,
                        })}
                      </Text>
                    </ListItem>
                  ))
                ) : (
                  <ListItem fontSize="14px" color="#484848" display="flex" alignItems="flex-start" gap="6px">
                    <Text as="span" flexShrink={0}>•</Text>
                    <Text as="span">{t("templateSensorReadingsExample")}</Text>
                  </ListItem>
                )}
                <ListItem
                  fontSize="14px"
                  color="#484848"
                  display="flex"
                  alignItems="flex-start"
                  gap="6px"
                >
                  <Text as="span" flexShrink={0}>•</Text>
                  <Text as="span">{t("templateInoperantSensors")}</Text>
                </ListItem>
              </List>
            </Box>

            {/* Event reference */}
            <Flex direction="column" gap="2px">
              <Text fontWeight="600" fontSize="14px" color="#1A1A1A">
                {t("templateEventLabel")}
              </Text>
              <Text fontSize="14px" color="#484848">
                {displayEventName}
                {displayLocation ? ` ${t("templateLocationPreposition")} ${displayLocation}` : ""}
              </Text>
            </Flex>
          </Box>

          <Button
            layerStyle="ocean"
            _hover={{ layerStyle: "ocean" }}
            onClick={onClose}
            width="100%"
          >
            {t("gotIt")}
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
